import React, { useState } from "react";
import api from "../api";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNotification } from "../context/NotificationContext";

const RegisterPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { showToast } = useNotification();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    phoneNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const { username, email, phoneNumber, password } = formData;

    // Username validation: min 3 chars, English only (alphanumeric and underscores)
    const usernameRegex = /^[a-zA-Z0-9_]{3,}$/;
    if (!usernameRegex.test(username)) {
      showToast("اسم المستخدم يجب أن يكون بالإنجليزية، 3 أحرف على الأقل، وبدون مسافات.", "warning");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("يرجى إدخال بريد إلكتروني صحيح.", "warning");
      return false;
    }

    // Phone number validation: simple Egyptian mobile format or international
    const phoneRegex = /^(?:\+2)?01[0125][0-9]{8}$/;
    if (!phoneRegex.test(phoneNumber.replace(/\s/g, ''))) {
      showToast("يرجى إدخال رقم هاتف صحيح (مثلاً: 01012345678).", "warning");
      return false;
    }

    // Password validation: min 6 chars
    if (password.length < 6) {
      showToast("كلمة المرور يجب أن تكون 6 أحرف على الأقل.", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await api.post("/api/auth/register", formData);
      showToast("تم التسجيل بنجاح!", "success");

      // Auto-login after registration
      const loginRes = await api.post("/api/auth/login", {
        email: formData.email,
        password: formData.password
      });

      const { accessToken, user } = loginRes.data;
      localStorage.setItem("youmak_token", accessToken);
      localStorage.setItem("youmak_user", JSON.stringify(user));
      if (onLogin) onLogin(user);

      // Redirect to client dashboard
      navigate("/client");
    } catch (err) {
      showToast(err.response?.data?.message || "حصل مشكلة، حاول تاني.", "danger");
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light px-3" dir="rtl" style={{ background: 'var(--bg)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-celebration p-4 p-md-5 w-100 shadow-lg border border-primary-dim text-center"
          style={{ maxWidth: '600px' }}
        >
          <div className="mb-4 flex justify-center">
            <div className="w-20 h-20 bg-success-container text-success rounded-circle flex items-center justify-center mx-auto shadow-sm">
              <span className="material-symbols-outlined display-1" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
            </div>
          </div>

          <h1 className="h2 fw-black text-dark mb-3">تم استلام طلبك بنجاح!</h1>
          <p className="text-muted fs-5 arabic-leading mb-5 px-3">
            أهلاً بك في يومك! عشان تفعل حسابك وتبدأ تعمل مناسبات، من فضلك تواصل مع الإدارة لتأكيد الاشتراك وتفعيل لوحة التحكم.
          </p>

          <div className="row g-3 mb-5">
            <div className="col-12 col-sm-6">
              <a className="d-flex align-items-center gap-3 p-4 bg-light rounded-4 text-decoration-none border hover-primary transition-soft group" href="https://wa.me/201013201998" target="_blank" rel="noreferrer">
                <div className="w-10 h-10 rounded-3 bg-white shadow-sm flex items-center justify-center text-green-600 shrink-0 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>chat</span>
                </div>
                <div className="text-end">
                  <span className="d-block small text-muted font-bold uppercase tracking-wider">WhatsApp</span>
                  <span className="d-block fw-black text-dark" dir="ltr">+20 1013201998</span>
                </div>
              </a>
            </div>
            <div className="col-12 col-sm-6">
              <a className="d-flex align-items-center gap-3 p-4 bg-light rounded-4 text-decoration-none border hover-primary transition-soft group" href="mailto:youmak.egp@gmail.com">
                <div className="w-10 h-10 rounded-3 bg-white shadow-sm flex items-center justify-center text-primary shrink-0 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>mail</span>
                </div>
                <div className="text-end">
                  <span className="d-block small text-muted font-bold uppercase tracking-wider">Email</span>
                  <span className="d-block fw-black text-dark">youmak.egp</span>
                </div>
              </a>
            </div>
          </div>

          <Link className="btn btn-primary btn-lg w-100 py-3 rounded-pill shadow-xl d-flex align-items-center justify-content-center gap-3 border-0 fw-black" to="/login">
            <span className="material-symbols-outlined fs-5">login</span>
            تسجيل الدخول
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light px-3 py-5" dir="rtl" style={{ background: 'var(--bg)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-celebration shadow-lg p-4 p-md-5 w-100"
        style={{ maxWidth: '600px', border: '1px solid var(--primary-dim)' }}
      >
        <div className="text-center mb-5">
          <div className="text-primary fw-black display-4 mb-3" style={{ fontFamily: 'var(--font-headline)' }}>يومك</div>
          <h1 className="h2 fw-black text-dark mb-2">اشتراك منظم جديد</h1>
          <p className="text-muted">خطوة واحدة تفصلك عن امتلاك لوحة تحكم مناسبتك الخاصة.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="username" className="form-label fw-bold text-muted small mb-2 d-block text-end">اسم المستخدم (English)</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-0"><span className="material-symbols-outlined text-muted fs-5">person</span></span>
              <input
                id="username"
                className="form-control bg-light border-0 py-3"
                dir="ltr"
                placeholder="e.g. jameel_ahmad"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="form-label fw-bold text-muted small mb-2 d-block text-end">البريد الإلكتروني</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-0"><span className="material-symbols-outlined text-muted fs-5">mail</span></span>
              <input
                id="email"
                className="form-control bg-light border-0 py-3"
                dir="ltr"
                placeholder="example@domain.com"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="phoneNumber" className="form-label fw-bold text-muted small mb-2 d-block text-end">رقم الهاتف</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-0"><span className="material-symbols-outlined text-muted fs-5">phone_iphone</span></span>
              <input
                id="phoneNumber"
                className="form-control bg-light border-0 py-3"
                dir="ltr"
                placeholder="+20 100 000 0000"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="form-label fw-bold text-muted small mb-2 d-block text-end">كلمة المرور</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-0"><span className="material-symbols-outlined text-muted fs-5">lock</span></span>
              <input
                id="password"
                className="form-control bg-light border-0 py-3"
                dir="ltr"
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <button
                className="btn btn-light d-flex align-items-center justify-content-center px-3"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ borderTopLeftRadius: '0.75rem', borderBottomLeftRadius: '0.75rem' }}
              >
                <span className="material-symbols-outlined fs-5">{showPassword ? "visibility_off" : "visibility"}</span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 py-3 fw-black fs-5 shadow-sm mt-3"
            disabled={loading}
          >
            {loading ? "جاري الاشتراك..." : "اشتراك"}
          </button>
        </form>

        <div className="mt-5 text-center">
          <p className="mb-3">
            لديك حساب بالفعل؟
            <Link className="text-primary fw-bold text-decoration-none ms-2" to="/login">تسجيل الدخول</Link>
          </p>
          <hr className="my-4 opacity-10" />
          <Link to="/" className="text-muted text-sm text-decoration-none hover-primary transition-soft d-inline-flex align-items-center gap-2">
            <span className="material-symbols-outlined fs-6">arrow_back</span>
            العودة للرئيسية
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
